import { Trade } from '@App/redux/slices/trade/trades/types'
import s from './styles.module.scss'
import cn from 'clsx'
import { formatDate, formatDateFromApi, formatTime, formatTimeFromApi, useCopyText, useSeeMarket } from '../../model'
import { CopyIcon } from '@App/static/icons/SortIcons'
import { Button } from '@App/ui/crypto_exchange/desktop/components/controls/Button'
import { getValueWithCurrency } from '@App/helpers/toCurrency'
import { MiniMarket, MiniOrder, MiniTrade, TradeTransaction, Transaction } from '@Redux/slices/reports/ReportsSlice'

export const TradeInfo = ({
  trade,
  market,
  onEditTrade,
}: {
  trade: MiniTrade | MiniOrder
  market: MiniMarket
  onEditTrade?: () => void
}) => {
  const { copyText } = useCopyText()
  const { seeMarket } = useSeeMarket({
    sourceCurrency: market.source_instrument_name,
    targetCurrency: market.target_instrument_code,
  })
  return (
    <div className={s.content}>
      <div className={s.general}>
        <div className={s.id}>
          ID:{trade.front_id} <CopyIcon className={s.icon} onClick={() => copyText(trade.front_id)} />
        </div>
        <div className={s.details}>
          <div className={cn(s.section, s.border)}>
            <div className={s.value}>{formatDateFromApi(trade.open_time)}</div>
            <div className={s.subValue}>{formatTimeFromApi(trade.open_time)}</div>
          </div>
          <div className={s.section}>
            <div className={s.value}>
              {market.source_instrument_name}/{market.target_instrument_code}
            </div>
            <div className={s.subValue}>
              {market.source_instrument_name} to {market.target_instrument_name}
            </div>
          </div>
        </div>
        <div className={cn(s.info, s.border)}>
          <div className={s.title}>Triggered by</div>
          <div className={s.value}>User</div>
        </div>
        <div className={s.info}>
          <div className={s.title}>Currency</div>
          <div className={s.value}>{market.target_instrument_code}</div>
        </div>
        <div className={s.actions}>
          <Button variant="outlined" onClick={seeMarket}>
            See market
          </Button>
          {trade.status === 'open' && (
            <Button variant="text" onClick={onEditTrade}>
              Edit trade
            </Button>
          )}
        </div>
      </div>
      <div className={s.history}>
        <div className={s.item}>
          <div className={s.header}>
            <div className={s.time}>
              {formatDateFromApi(trade.open_time)} {formatTimeFromApi(trade.open_time)}
            </div>
            <div className={s.title}>
              Opened
              <div className={cn(s.quantity, trade.action_type === 'buy' && s.buy)}>
                {trade.action_type === 'buy' ? '+' : '-'}
                {trade.quantity}
              </div>
              {market.source_instrument_name}/{market.target_instrument_code}
            </div>
          </div>
          <div className={cn(s.info, s.borderTop)}>
            <div className={s.title}>At price</div>
            <div className={s.value}>{getValueWithCurrency(trade.currency, trade.open_rate)}</div>
          </div>
        </div>
        {trade.status === 'closed' && (
          <div className={s.item}>
            <div className={s.header}>
              <div className={s.time}>
                {formatDateFromApi(trade.open_time)} {formatTimeFromApi(trade.open_time)}
              </div>
              <div className={s.title}>
                Closed{' '}
                <div className={cn(s.quantity, trade.action_type !== 'buy' && s.buy)}>
                  {' '}
                  {trade.action_type !== 'buy' ? '+' : '-'}
                  {trade.quantity}
                </div>{' '}
                {market.source_instrument_name}/{market.target_instrument_code}
              </div>
            </div>
            <div className={cn(s.info, s.borderTop)}>
              <div className={s.title}>At price</div>
              <div className={s.value}>{getValueWithCurrency(trade.currency, trade?.close_rate ?? 0)}</div>
            </div>
            <div className={cn(s.info, s.borderTop, s.border)}>
              <div className={s.title}>Coversion rate</div>
              <div className={s.value}>1</div>
            </div>
            <div className={cn(s.info, s.borderTop)}>
              <div className={s.title}>Total</div>
              <div className={cn(s.value, trade.result < 0 ? s.negative : s.positive)}>
                {getValueWithCurrency(trade.currency, trade?.result ?? 0)}
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  )
}
